import { Injectable } from '@angular/core';
import { ApiService } from 'app/core/api/api.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, ReplaySubject, tap } from 'rxjs';

/** Menu laterale: le voci cambiano in base al ruolo (gli amministratori vedono anche utenti e configurazione). */
@Injectable({providedIn: 'root'})
export class NavigationService
{
    private _navigation = new ReplaySubject<Navigation>(1);
    /** Prenotazioni arrivate dall'ultima visita al registro: compaiono come pallino rosso accanto alla voce "Prenotazioni". */
    private _unseen = 0;
    private _timer?: ReturnType<typeof setInterval>;

    constructor(private _userService: UserService, private _api: ApiService)
    {
    }

    /** Aggiorna il pallino delle nuove prenotazioni (si richiama aprendo il registro e ogni minuto). */
    refreshBadge(): void
    {
        this._api.get<{ count: number }>('api/admin/tickets/unseen').pipe(catchError((e) =>
        {
            // sessione scaduta: smetto di interrogare l'API finché non si rientra
            if ( e?.status === 401 && this._timer )
            {
                clearInterval(this._timer);
                this._timer = undefined;
            }
            return of({count: this._unseen});
        })).subscribe((r) =>
        {
            if ( r.count !== this._unseen )
            {
                this._unseen = r.count;
                this._navigation.next(this._build());
            }
        });
    }

    private _build(): Navigation
    {
        const navigation = this._buildItems();
        return {compact: navigation, default: navigation, futuristic: navigation, horizontal: navigation};
    }

    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    get(): Observable<Navigation>
    {
        // il pallino si aggiorna da solo ogni minuto mentre l'area riservata è aperta
        this.refreshBadge();
        this._timer ??= setInterval(() => this.refreshBadge(), 60000);
        return of(this._build()).pipe(tap((value) => this._navigation.next(value)));
    }

    private _buildItems(): FuseNavigationItem[]
    {
        const isAdmin = this._userService.isAdmin;

        const main: FuseNavigationItem[] = [
            {id: 'home', title: 'Home', type: 'basic', icon: 'heroicons_outline:home', link: '/dashboard'},
            {id: 'partners', title: 'Annunci', type: 'basic', icon: 'heroicons_outline:building-storefront', link: '/partners'},
            {id: 'lastminute', title: 'Last Minute', type: 'basic', icon: 'heroicons_outline:bolt', link: '/last-minute', exactMatch: false},
            {id: 'coupons', title: 'Sconti', type: 'basic', icon: 'heroicons_outline:receipt-percent', link: '/coupons', exactMatch: false},
            {
                id: 'tickets', title: 'Prenotazioni', type: 'basic', icon: 'heroicons_outline:calendar-days', link: '/tickets', exactMatch: false,
                badge: this._unseen > 0 ? {title: this._unseen > 99 ? '99+' : String(this._unseen), classes: 'px-2 bg-red-600 text-white rounded-full'} : undefined,
            },
            {id: 'subscriptions', title: 'Abbonamenti', type: 'basic', icon: 'heroicons_outline:credit-card', link: '/subscriptions'},
            {id: 'interactions', title: 'Interazioni e Network', type: 'basic', icon: 'heroicons_outline:cursor-arrow-rays', link: '/interactions', exactMatch: false},
        ];

        if ( isAdmin )
        {
            main.push({id: 'users', title: 'Utenti', type: 'basic', icon: 'heroicons_outline:users', link: '/users'});
            main.push({id: 'promo', title: 'Sconti e promozioni', type: 'basic', icon: 'heroicons_outline:ticket', link: '/promo', exactMatch: false});
            main.push({
                id: 'config', title: 'Configurazione', type: 'aside', icon: 'heroicons_outline:cog-8-tooth',
                children: [
                    {id: 'config.categories', title: 'Categorie annunci', type: 'basic', link: '/config/categories'},
                    {id: 'config.service-categories', title: 'Categorie servizi', type: 'basic', link: '/config/service-categories'},
                    {id: 'config.services', title: 'Servizi', type: 'basic', link: '/config/services'},
                    {id: 'config.plans', title: 'Pacchetti abbonamento', type: 'basic', link: '/config/plans'},
                    {id: 'config.payments', title: 'Pagamenti PayPal', type: 'basic', link: '/config/payments'},
                    {id: 'config.activity-log', title: 'Log attività', type: 'basic', link: '/config/activity-log'},
                    {id: 'config.search-log', title: 'Log ricerche', type: 'basic', link: '/config/search-log'},
                    {id: 'config.log-mail', title: 'Log email', type: 'basic', link: '/config/log-mail'},
                ],
            });
        }
        else
        {
            // il partner trova qui il suo codice invito e i suoi punti (le categorie del listino si gestiscono dalla pagina Listino)
            main.push({id: 'partner-area', title: 'Area Partner', type: 'basic', icon: 'heroicons_outline:gift', link: '/partner-area'});
        }

        return main;
    }
}
